import { useAppState } from 'hooks/useAppState';
import Index from 'pages/Index';
import Error404 from 'pages/error/Error404';
import InvitationIndex from 'pages/invitation/index';
import InvitationUser from 'pages/invitation/user';
import ProxyLogin from 'pages/proxy/login';
import ProxyCallback from 'pages/proxy/callback';
import SettingCompany from 'pages/setting/company';
import SettingMember from 'pages/setting/member';
import SettingMemberEdit from 'pages/setting/memberEdit';
import SettingProfile from 'pages/setting/profile';
import SettingLayout from 'pages/setting/components/settingLayout/SettingLayout';
import SigninIndex from 'pages/signin/index';
import SigninAuth from 'pages/signin/auth';
import SigninCompany from 'pages/signin/company';
import SignupIndex from 'pages/signup/index';
import SignupOldCompany from 'pages/signupOld/company';
import SignupOldIndex from 'pages/signupOld/index';
import WsContentSearch from 'pages/ws/contentSearch';
import WsDiagnosisEdit from 'pages/ws/diagnosisEdit';
import WsFormEdit from 'pages/ws/formEdit';
import WsIndex from 'pages/ws/index';
import WsQuizEdit from 'pages/ws/quizEdit';
import WsReportDiagnosis from 'pages/ws/reportDiagnosis';
import WsReportQuiz from 'pages/ws/reportQuiz';
import WsReportForm from 'pages/ws/reportForm';
import WsReportSearch from 'pages/ws/reportSearch';
import WsRespondentDiagnosis from 'pages/ws/respondentDiagnosis';
import WsRespondentQuiz from 'pages/ws/respondentQuiz';
import WsRespondentForm from 'pages/ws/respondentForm';
import WsRespondentSearch from 'pages/ws/respondentSearch';
import WsVoiceChatEdit from 'pages/ws/voiceChatEdit/WsVoiceChatEdit';
import WsReportVoiceChat from 'pages/ws/reportVoiceChat/WsReportVoiceChat';
import WsLayout from 'pages/ws/components/layout/WsLayout';
import { useLayoutEffect, useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
/**
 * アプリケーション共通 Router定義
 */
export default function AppRoutes(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const location = useLocation();
  const navigate = useNavigate();

  const [acctState] = useAtom(AcctState);

  const { initializeAcctState } = useAppState();

  const [initialized, setInitialized] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useLayoutEffect(() => {
    // URLからカンパニーCDを取得
    const matchs = location.pathname.match(/^\/(ws|setting)\/([^/]+).*$/);
    const companyCd = matchs && matchs.length == 3 ? matchs[2] : null;

    // 初期読み込みまたは戻るボタンの制御
    // eslint-disable-next-line react-hooks/immutability -- _isStateNotInitializedは下方で宣言されているが関数巻き上げ相当で利用
    if (_isStateNotInitialized()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- 初期化制御のため意図的な同期setState
      setInitialized(false);
    }
    (async () => {
      try {
        if (!acctState.initialized) {
          // 認証済みでログイン画面に遷移する場合はホーム画面に遷移
          if ((await initializeAcctState(companyCd)).company != null) {
            if (location.pathname.startsWith('/signin')) {
              navigate('/');
            }
          }
          // 未認証でかつ認証が必要な画面にアクセスする場合はログイン画面に遷移
          else {
            // eslint-disable-next-line react-hooks/immutability -- _isRequiresAuthenticationは下方で宣言されているが関数巻き上げ相当で利用
            if (_isRequiresAuthentication(location.pathname)) {
              navigate('/signin', {
                state: { referer: location.pathname },
              });
            }
          }
        }
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ページ遷移 (location.pathname 変化) のたびに再認証する設計のため、関数依存は意図的に省略する
  }, [location.pathname]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _isRequiresAuthentication = (pathname: string): boolean => {
    if (!pathname) {
      return true;
    }
    if (pathname.startsWith('/ws/')) {
      return true;
    }
    if (pathname.startsWith('/setting/')) {
      return true;
    }

    return false;
  };

  const _isStateNotInitialized = (): boolean => !acctState.initialized;

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {initialized && (
        <Routes>
          {acctState.initialized ? (
            <>
              <Route path="/" element={<Index />} />
              {/* カンパニー選択画面 */}
              <Route path="/signin/company/" element={<SigninCompany />} />
              {/* ログイン認証コード入力画面 */}
              <Route path="/signin/auth/" element={<SigninAuth />} />
              {/* アカウント登録画面 */}
              <Route path="/signup/" element={<SignupIndex />} />
              {/* (旧)アカウント登録画面 */}
              <Route path="/signupOld/" element={<SignupOldIndex />} />
              <Route path="/signupOld/company/" element={<SignupOldCompany />} />
              {/* 招待受付画面 */}
              <Route path="/invitation/:invitationCd/" element={<InvitationIndex />} />
              {/* 招待受付ユーザー情報入力画面 */}
              <Route path="/invitation/:invitationCd/user" element={<InvitationUser />} />
              {/* 代理ログイン画面 */}
              <Route path="/proxy/:companyCd/" element={<ProxyLogin />} />
              {/* 代理ログイン画面 */}
              <Route path="/proxy/callback/" element={<ProxyCallback />} />
              {/* 404エラー画面 */}
              <Route path="*" element={<Error404 />} />
            </>
          ) : (
            ''
          )}
          {/* ログイン済の場合のみ表示可能な画面 */}
          {acctState.initialized && acctState.company != null ? (
            <>
              <Route path="/setting/:companyCd/" element={<SettingLayout />}>
                {/* プロフィール設定画面 */}
                <Route path="/setting/:companyCd/profile" element={<SettingProfile />} />
                {/* カンパニー設定画面 */}
                <Route path="/setting/:companyCd/company" element={<SettingCompany />} />
                {/* カンパニーメンバー設定画面 */}
                <Route path="/setting/:companyCd/member" element={<SettingMember />} />
                {/* カンパニーメンバー詳細画面 */}
                <Route path="/setting/:companyCd/member/edit" element={<SettingMemberEdit />} />
              </Route>

              <Route path="/ws/:companyCd/" element={<WsLayout />}>
                {/* ホーム画面 */}
                <Route path="/ws/:companyCd/" element={<WsIndex />} />
                {/* コンテンツ画面 */}
                <Route path="/ws/:companyCd/content/search/" element={<WsContentSearch />} />
                <Route path="/ws/:companyCd/content/search/:pageNo" element={<WsContentSearch />} />
                <Route path="/ws/:companyCd/content/search" element={<WsContentSearch />} />
                {/* クイズ作成・編集画面 */}
                <Route path="/ws/:companyCd/quiz/edit" element={<WsQuizEdit />} />
                <Route path="/ws/:companyCd/quiz/edit/:quizCd" element={<WsQuizEdit />} />
                {/* 診断作成・編集画面 */}
                <Route path="/ws/:companyCd/diagnosis/edit" element={<WsDiagnosisEdit />} />
                <Route
                  path="/ws/:companyCd/diagnosis/edit/:diagnosisCd"
                  element={<WsDiagnosisEdit />}
                />
                {/* フォーム作成・編集画面 */}
                <Route path="/ws/:companyCd/form/edit" element={<WsFormEdit />} />
                <Route path="/ws/:companyCd/form/edit/:formCd" element={<WsFormEdit />} />
                {/* ボイスチャット作成・編集画面 */}
                <Route path="/ws/:companyCd/voice_chat/edit" element={<WsVoiceChatEdit />} />
                <Route
                  path="/ws/:companyCd/voice_chat/edit/:voiceChatCd"
                  element={<WsVoiceChatEdit />}
                />

                {/* 獲得リスト画面 */}
                <Route path="/ws/:companyCd/respondent/search/" element={<WsRespondentSearch />} />
                <Route
                  path="/ws/:companyCd/respondent/search/:pageNo"
                  element={<WsRespondentSearch />}
                />
                {/* クイズ獲得リスト詳細画面 */}
                <Route
                  path="/ws/:companyCd/respondent/quiz/:quizCd"
                  element={<WsRespondentQuiz />}
                />
                {/* 診断獲得リスト詳細画面 */}
                <Route
                  path="/ws/:companyCd/respondent/diagnosis/:diagnosisCd"
                  element={<WsRespondentDiagnosis />}
                />
                {/* フォーム獲得リスト詳細画面 */}
                <Route
                  path="/ws/:companyCd/respondent/form/:formCd"
                  element={<WsRespondentForm />}
                />
                {/* レポート画面 */}
                <Route path="/ws/:companyCd/report/search/" element={<WsReportSearch />} />
                <Route path="/ws/:companyCd/report/search/:pageNo" element={<WsReportSearch />} />
                {/* クイズレポート詳細画面 */}
                <Route path="/ws/:companyCd/report/quiz/:quizCd" element={<WsReportQuiz />} />
                {/* 診断レポート詳細画面 */}
                <Route
                  path="/ws/:companyCd/report/diagnosis/:diagnosisCd"
                  element={<WsReportDiagnosis />}
                />
                {/* フォームレポート詳細画面 */}
                <Route path="/ws/:companyCd/report/form/:formCd" element={<WsReportForm />} />
                {/* ボイスチャットレポート詳細画面 */}
                <Route
                  path="/ws/:companyCd/report/voice_chat/:voiceChatCd"
                  element={<WsReportVoiceChat />}
                />
              </Route>
            </>
          ) : (
            ''
          )}
          {/* 未ログインの場合のみ表示可能な画面 */}
          {acctState.initialized && acctState.company == null ? (
            <>
              {/* ログイン画面 */}
              <Route path="/signin/" element={<SigninIndex />} />
            </>
          ) : (
            ''
          )}
        </Routes>
      )}
    </>
  );
}
